<script setup lang="ts">
import { computed, ref, onMounted, useTemplateRef } from "vue";
import { useRoute } from "vue-router";
import { definePage } from "unplugin-vue-router/runtime";
import { STLViewer } from "@blob/threejs-viewer";
import "@blob/threejs-viewer/style.css";
import {
  useBlobStlViewerLoadFiles,
  type BlobStlViewerExposed,
} from "@/composables/shared/useBlobStlViewerLoadFiles.ts";

definePage({
  meta: {
    layout: false,
    requiresAuth: true,
  },
});

const route = useRoute();

const fileIds = computed(() => {
  const idsParam = (route.params as { ids?: string }).ids;
  if (!idsParam) return [];
  return idsParam.split(",").filter((id) => id.trim().length > 0);
});

const files = ref<File[]>([]);
const stlViewerRef = useTemplateRef<BlobStlViewerExposed>("stlViewer");
useBlobStlViewerLoadFiles(files, stlViewerRef);
const isLoadingFiles = ref(false);
const loadError = ref<string | null>(null);
const hasEverLoadedFiles = ref(false);

const loadFiles = async () => {
  if (fileIds.value.length === 0) {
    files.value = [];
    return;
  }
};

const shouldRenderSTLViewer = computed(() => {
  return !isLoadingFiles.value && (files.value.length > 0 || hasEverLoadedFiles.value);
});

onMounted(() => {
  loadFiles();
});
</script>

<template>
  <div class="stl-viewer-fullscreen tw:fixed tw:inset-0 tw:z-app-overlay">
    <div
      v-if="isLoadingFiles"
      class="tw:flex tw:items-center tw:justify-center tw:h-full"
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
    </div>
    <STLViewer
      v-else-if="shouldRenderSTLViewer"
      ref="stlViewer"
      height="100%"
      width="100%"
      background-color="#eeeeee"
      theme="bright"
    />
    <div
      v-else-if="!isLoadingFiles && !shouldRenderSTLViewer"
      class="tw:flex tw:flex-col tw:items-center tw:justify-center tw:h-full tw:text-gray-400 tw:gap-4"
    >
      <div
        v-if="loadError"
        class="tw:text-red-500"
      >
        {{ loadError }}
      </div>
      <div v-else> No files to display</div>
    </div>
  </div>
</template>

<style scoped>
.stl-viewer-fullscreen {
  padding-top: env(safe-area-inset-top);
  padding-right: env(safe-area-inset-right);
  padding-bottom: env(safe-area-inset-bottom);
  padding-left: env(safe-area-inset-left);
  box-sizing: border-box;
}
</style>
